import {
  createFileRoute,
  useNavigate,
  useSearch,
} from "@tanstack/react-router";
import { useUpcomingEvents } from "@/lib/api/upcoming.ts";
import { Loader2 } from "lucide-react";
import { UpcomingEventCard } from "@/components/upcoming/upcoming-event-card.tsx";
import { MyMenteesSelect } from "@/components/upcoming/my-mentees-select.tsx";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select.tsx";
import { Fragment, useMemo } from "react";
import { PostHogEvents, useTrackPage } from "@/lib/posthog-events";

export const Route = createFileRoute("/_authenticated/upcoming")({
  component: UpcomingPage,
});

const DEFAULT_MENTEE_IDS = JSON.stringify(["all"]);

function UpcomingPage() {
  const navigate = useNavigate();
  const { menteeIds = DEFAULT_MENTEE_IDS, type = "all" } = useSearch({
    strict: false,
  }) as Record<string, string>;
  useTrackPage(PostHogEvents.PAGE_UPCOMING);

  const menteeIdsArray: string[] = useMemo(() => {
    try {
      return JSON.parse(menteeIds);
    } catch (e) {
      return [];
    }
  }, [menteeIds]);

  const menteeIdsParam = useMemo(() => {
    return menteeIdsArray.length
      ? JSON.stringify(menteeIdsArray.filter((id) => id !== "all"))
      : undefined;
  }, [menteeIdsArray]);

  const { data, isLoading, isError } = useUpcomingEvents(
    menteeIdsParam,
    type !== "all" ? (type as "session" | "note") : undefined,
  );

  const handleFilterChange = (key: string, value: string | string[]) => {
    navigate({
      search: {
        menteeIds: key === "menteeIds" ? JSON.stringify(value) : menteeIds,
        type: key === "type" ? value : type,
      } as any,
    });
  };

  return (
    <div className="container max-w-4xl bg-white py-4">
      <h1 className="my-6 text-2xl font-bold leading-7 text-black">Upcoming</h1>

      <div className="mb-8 mt-4 grid grid-cols-3 gap-4">
        <MyMenteesSelect
          menteeIds={menteeIdsArray}
          handleFilterChange={handleFilterChange}
        />

        <Select
          value={type}
          onValueChange={(newType) => {
            handleFilterChange("type", newType);
          }}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            <SelectItem value="session">Sessions</SelectItem>
            <SelectItem value="note">Reminders</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <div className="flex min-h-[400px] w-full items-center justify-center bg-primary-100">
          <Loader2 className="h-12 w-12 animate-spin text-blue-brand" />
        </div>
      ) : isError || !data ? (
        <div className="flex min-h-[400px] w-full items-center justify-center bg-primary-100">
          <p className="text-blue-brand">
            Something went wrong! Please try again!
          </p>
        </div>
      ) : (
        <div className="flex flex-col">
          <div className="mb-8 flex flex-col">
            <h2 className="mb-4 text-base font-bold leading-tight text-black">
              Today
            </h2>

            {data.eventsForToday.length > 0 ? (
              <Fragment>
                {data.eventsForToday.map((event) => (
                  <UpcomingEventCard upcomingEvent={event} key={event.id} />
                ))}
              </Fragment>
            ) : (
              <div className="text-black">
                <p>No events for today</p>
              </div>
            )}
          </div>

          <div className="flex flex-col gap-4">
            <h2 className="mb-4 text-base font-bold leading-tight text-black">
              Upcoming
            </h2>

            {data.restOfTheEvents.length > 0 ? (
              <Fragment>
                {data.restOfTheEvents.map((event) => (
                  <UpcomingEventCard upcomingEvent={event} key={event.id} />
                ))}
              </Fragment>
            ) : (
              <div className="text-black">
                <p>No upcoming events</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
